import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Button,
  Heading,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
  Image,
  Checkbox,
  Link,
  CloseButton,
  HStack,
  Text,
  Flex,
  SimpleGrid,
  Icon,
  Fade,
  Skeleton,
  SkeletonText,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { LANGUAGE_CODES } from "./constants/languages";
import {
  FaGlobe,
  FaHandshake,
  FaChartLine,
  FaQuestionCircle,
  FaDatabase,
  FaUpload,
  FaNewspaper,
  FaExchangeAlt,
  FaMapMarkedAlt,
  FaProjectDiagram,
  FaLanguage,
  FaRobot,
  FaIdBadge,
} from "react-icons/fa";

// Centralized API base + headers
const API_BASE = "https://api.globalwineinstitute.org";
const DEFAULT_HEADERS = {
  "X-API-KEY": "f3bb4afa55070cc052d1b888d4f7465a",
};

// ---- Auth token storage helper (Option B: persistent login) ----
const tokenStorage = {
  set(token, persistent) {
    const store = persistent ? localStorage : sessionStorage;
    if (token) store.setItem("authToken", token);
    const other = persistent ? sessionStorage : localStorage;
    other.removeItem("authToken");
  },
  get() {
    return (
      localStorage.getItem("authToken") ?? sessionStorage.getItem("authToken")
    );
  },
  clear() {
    localStorage.removeItem("authToken");
    sessionStorage.removeItem("authToken");
  },
};

function renderMarkdownLogin(text) {
  if (!text) return null;
  const lines = text.split("\n");
  const elements = [];
  let paraLines = [];

  const flushPara = () => {
    if (paraLines.length === 0) return;
    const raw = paraLines.join(" ");
    paraLines = [];
    const parts = raw.split(/(\*\*.*?\*\*|\*.*?\*)/g).map((seg, j) => {
      if (seg.startsWith("**") && seg.endsWith("**"))
        return <Text as="span" key={j} fontWeight="700" color="white">{seg.slice(2, -2)}</Text>;
      if (seg.startsWith("*") && seg.endsWith("*"))
        return <Text as="span" key={j} fontStyle="italic">{seg.slice(1, -1)}</Text>;
      return seg;
    });
    elements.push(
      <Text key={elements.length} fontSize="xs" color="whiteAlpha.900" lineHeight="1.7" mb={1.5}>
        {parts}
      </Text>
    );
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (/^#{1,3}\s+/.test(trimmed)) {
      flushPara();
      const title = trimmed.replace(/^#{1,3}\s+/, "");
      elements.push(
        <Text
          key={elements.length}
          fontSize="sm"
          fontWeight="800"
          color="gwbi.300"
          mt={elements.length > 0 ? 3 : 0}
          mb={1.5}
          letterSpacing="0.01em"
        >
          {title}
        </Text>
      );
    } else if (trimmed === "") {
      flushPara();
    } else {
      paraLines.push(trimmed);
    }
  }
  flushPara();
  return elements;
}

const VisionCard = ({ openRegistration }) => {
  const { t } = useTranslation();
  // Slides: each slide is a set of 2–3 mini-cards (title, subtitle, icon)
  const slides = React.useMemo(
    () => [
      // Slide 0 — the original three pillars
      [
        {
          icon: FaGlobe,
          title: t("login_slides.global_reach.title"),
          subtitle: t("login_slides.global_reach.subtitle"),
        },
        {
          icon: FaHandshake,
          title: t("login_slides.collaboration.title"),
          subtitle: t("login_slides.collaboration.subtitle"),
        },
        {
          icon: FaChartLine,
          title: t("login_slides.insights.title"),
          subtitle: t("login_slides.insights.subtitle"),
        },
      ],
      // Slide 1 — engagement features
      [
        {
          icon: FaQuestionCircle,
          title: t("login_slides.ask_expert.title"),
          subtitle: t("login_slides.ask_expert.subtitle"),
        },
        {
          icon: FaExchangeAlt,
          title: t("login_slides.reverse_seminars.title"),
          subtitle: t("login_slides.reverse_seminars.subtitle"),
        },
        {
          icon: FaNewspaper,
          title: t("login_slides.weekly_digest.title"),
          subtitle: t("login_slides.weekly_digest.subtitle"),
        },
      ],
      // Slide 2 — data & research
      [
        {
          icon: FaDatabase,
          title: t("login_slides.winery_data.title"),
          subtitle: t("login_slides.winery_data.subtitle"),
        },
        {
          icon: FaUpload,
          title: t("login_slides.share_research.title"),
          subtitle: t("login_slides.share_research.subtitle"),
        },
        {
          icon: FaIdBadge,
          title: t("login_slides.scholarly_profiles.title"),
          subtitle: t("login_slides.scholarly_profiles.subtitle"),
        },
      ],
      // Slide 3 — structure & scale
      [
        {
          icon: FaProjectDiagram,
          title: t("login_slides.themes.title"),
          subtitle: t("login_slides.themes.subtitle"),
        },
        {
          icon: FaMapMarkedAlt,
          title: t("login_slides.regional_clusters.title"),
          subtitle: t("login_slides.regional_clusters.subtitle"),
        },
        {
          icon: FaLanguage,
          title: t("login_slides.multilingual.title"),
          subtitle: t("login_slides.multilingual.subtitle"),
        },
      ],
      // Slide 4 — content engine
      [
        {
          icon: FaRobot,
          title: t("login_slides.smart_curation.title"),
          subtitle: t("login_slides.smart_curation.subtitle"),
        },
        {
          icon: FaChartLine,
          title: t("login_slides.actionable_insights.title"),
          subtitle: t("login_slides.actionable_insights.subtitle"),
        },
        {
          icon: FaHandshake,
          title: t("login_slides.collaboration_board.title"),
          subtitle: t("login_slides.collaboration_board.subtitle"),
        },
      ],
    ],
    [t],
  );

  const [slideIndex, setSlideIndex] = React.useState(0);
  React.useEffect(() => {
    if (slides.length === 0) return;
    const interval = setInterval(() => {
      setSlideIndex((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [slides.length]);

  return (
    <Box
      bg="rgba(255, 255, 255, 0.15)"
      backdropFilter="blur(12px)"
      borderRadius="2xl"
      p={{ base: 5, md: 8 }}
      boxShadow="0 8px 32px rgba(0, 0, 0, 0.2)"
      border="1px solid rgba(255, 255, 255, 0.2)"
      flex="1 1 auto"
      width="100%"
      height="100%"
      position="static"
      display="flex"
      flexDirection="column"
      justifyContent="flex-start"
      textAlign="center"
      mt={0}
    >
      <Heading size="lg" mb={1} color="white" letterSpacing="tight">
        {t("app_title")}
      </Heading>

      {/* Carousel of mini-cards (cards within the card) */}
      <Box
        mt={6}
        overflow="hidden"
        display="flex"
        flexDirection="column"
        flex="1"
      >
        <Fade
          key={slideIndex}
          in={true}
          transition={{ enter: { duration: 5.0 } }}
        >
          <SimpleGrid
            columns={{ base: 1, md: 3 }}
            spacing={4}
            flex="1"
            minH="200px"
            alignContent="stretch"
            sx={{ gridAutoRows: "1fr" }}
          >
            {slides[slideIndex].map((item, idx) => (
              <Box
                key={`mini-${slideIndex}-${idx}`}
                py={5}
                px={4}
                bg="rgba(255, 255, 255, 0.85)"
                backdropFilter="blur(8px)"
                borderRadius="xl"
                border="1px solid rgba(255, 255, 255, 0.5)"
                boxShadow="0 4px 20px rgba(0, 0, 0, 0.08)"
                _hover={{ transform: "translateY(-3px)", boxShadow: "0 8px 30px rgba(0, 0, 0, 0.15)" }}
                transition="all 0.3s ease"
                textAlign="center"
                h="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
              >
                <Icon
                  as={item.icon}
                  boxSize={7}
                  mb={3}
                  color="gwbi.500"
                />
                <Text
                  fontWeight="bold"
                  fontSize="sm"
                  lineHeight="short"
                  color="#3a1714"
                  wordBreak="keep-all"
                  sx={{ hyphens: "none" }}
                >
                  {item.title}
                </Text>
                <Text
                  fontSize="xs"
                  lineHeight="short"
                  color="gray.500"
                  mt={1}
                  wordBreak="keep-all"
                  sx={{ hyphens: "none" }}
                >
                  {item.subtitle}
                </Text>
              </Box>
            ))}
          </SimpleGrid>
        </Fade>

        {/* Bullets + prev/next */}
        <HStack justify="center" align="center" spacing={2} mt="auto" pt={4}>
          <Button
            size="xs"
            variant="ghost"
            color="whiteAlpha.800"
            _hover={{ bg: "whiteAlpha.200" }}
            onClick={() =>
              setSlideIndex((prev) =>
                prev === 0 ? slides.length - 1 : prev - 1,
              )
            }
            isDisabled={slides.length <= 1}
          >
            &lt;
          </Button>
          {slides.map((_, idx) => (
            <Box
              key={`slide-dot-${idx}`}
              w={idx === slideIndex ? 4 : 2}
              h={idx === slideIndex ? 4 : 2}
              borderRadius="full"
              bg={idx === slideIndex ? "gwbi.400" : "whiteAlpha.500"}
              mx={1}
              transition="all 0.4s ease"
            />
          ))}
          <Button
            size="xs"
            variant="ghost"
            color="whiteAlpha.800"
            _hover={{ bg: "whiteAlpha.200" }}
            onClick={() => setSlideIndex((prev) => (prev + 1) % slides.length)}
            isDisabled={slides.length <= 1}
          >
            &gt;
          </Button>
        </HStack>
      </Box>

      {/* <Box mt={6}>
        <Button size="sm" colorScheme="gwbi" onClick={openRegistration}>
          Learn more / Join
        </Button>
      </Box> */}
    </Box>
  );
};

const Login = ({ setIsLoggedIn: externalSetIsLoggedIn }) => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const localeMap = {
    en: "en-US",
    es: "es-ES",
    it: "it-IT",
    fr: "fr-FR",
    de: "de-DE",
    pt: "pt-PT",
  };
  const locale = localeMap[i18n.language] || "en-US";
  // If setIsLoggedIn is not provided, fall back to internal state for developer logout
  const [, setLocalIsLoggedIn] = useState(
    () => localStorage.getItem("isAuthenticated") === "true",
  );
  const setIsLoggedIn = externalSetIsLoggedIn || setLocalIsLoggedIn;
  const [username, setUsername] = useState("");
  const [rememberMe, setRememberMe] = useState(
    () => localStorage.getItem("rememberMe") === "true",
  );
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [shake, setShake] = useState(false);
  const [videoIndex] = useState(0);
  const [email] = useState("");
  const videoSources = [
    ["/wine1.mp4", "/wine2.mp4", "/wine3.mp4"][Math.floor(Math.random() * 3)],
  ];
  const [posts, setPosts] = useState([]);
  // Conferences state
  const [conferences, setConferences] = useState([]);
  const [conferenceIndex, setConferenceIndex] = useState(0); // Individual index for conferences
  // Publications state
  const [publications, setPublications] = useState([]);
  const [publicationIndex, setPublicationIndex] = useState(0); // Individual index for publications
  const [, setShowPublications] = useState(false);
  const [showRegister, setShowRegister] = useState(false);

  const [weeklySummary, setWeeklySummary] = useState(null);

  // Show News and Conferences cards with delay
  const [showNews, setShowNews] = useState(false);
  const [showConferences, setShowConferences] = useState(false);

  useEffect(() => {
    const insightTimeout = setTimeout(() => setShowNews(true), 4000);
    const confTimeout = setTimeout(() => setShowConferences(true), 8000);
    const pubTimeout = setTimeout(() => setShowPublications(true), 12000);
    return () => {
      clearTimeout(insightTimeout);
      clearTimeout(confTimeout);
      clearTimeout(pubTimeout);
    };
  }, []);

  useEffect(() => {
    const lang = (i18n.language || "en").slice(0, 2);
    fetch(`${API_BASE}/api/weekly-summary/latest?lang=${lang}`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d && d.year) setWeeklySummary(d); })
      .catch(() => {});
  }, [i18n.language]);

  // Detect and complete email verification via token in URL
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const token = url.searchParams.get("token");
      if (!token) return;

      fetch(
        `${API_BASE}/api/users/email-verify/complete?token=${encodeURIComponent(
          token,
        )}`,
        {
          method: "GET",
        },
      )
        .then(async (res) => {
          if (res.ok) {
            toast({
              title: t("login_text.email_verified_title"),
              description: t("login_text.email_verified_desc"),
              status: "success",
              duration: 6000,
              isClosable: true,
            });
          } else {
            let msg = "";
            try {
              msg = await res.text();
            } catch {}
            toast({
              title: t("login_text.verification_failed_title"),
              description:
                msg || t("login_text.verification_failed_desc_fallback"),
              status: "error",
              duration: 6000,
              isClosable: true,
            });
          }
        })
        .catch(() => {
          toast({
            title: t("login_text.network_error_title"),
            description: t("login_text.network_error_desc"),
            status: "error",
            duration: 6000,
            isClosable: true,
          });
        })
        .finally(() => {
          // Remove token param from URL so we don't re-trigger on navigation
          try {
            const clean = new URL(window.location.href);
            clean.searchParams.delete("token");
            window.history.replaceState({}, document.title, clean.toString());
          } catch {}
        });
    } catch {}
  }, []);

  // Fetch publications
  useEffect(() => {
    const controller = new AbortController();
    const fetchPublications = async () => {
      try {
        const response = await fetch(`${API_BASE}/wineries/publications`, {
          headers: DEFAULT_HEADERS,
          signal: controller.signal,
        });
        const data = await response.json();
        // Sort publications by most recent date first
        const sortedPubs = (Array.isArray(data) ? data.slice() : []).sort(
          (a, b) =>
            new Date(b?.pub_date || b?.date || 0) -
            new Date(a?.pub_date || a?.date || 0),
        );
        setPublications(sortedPubs);
      } catch (err) {}
    };
    fetchPublications();
    return () => controller.abort();
  }, []);

  // Cycle through publications (individual to publications)
  useEffect(() => {
    if (publications.length === 0) return;
    const interval = setInterval(() => {
      setPublicationIndex((prev) => (prev + 1) % publications.length);
    }, 20000);
    return () => clearInterval(interval);
  }, [publications]);

  // Fetch conferences
  useEffect(() => {
    const controller = new AbortController();
    const fetchConferences = async () => {
      try {
        const response = await fetch(`${API_BASE}/wineries/conferences`, {
          headers: DEFAULT_HEADERS,
          signal: controller.signal,
        });
        if (!response.ok) throw new Error("Failed to fetch events");
        const data = await response.json();
        setConferences(Array.isArray(data) ? data : []);
      } catch (error) {}
    };
    fetchConferences();
  }, []);

  // Cycle through conferences (individual to conferences)
  useEffect(() => {
    if (conferences.length === 0) return;
    const interval = setInterval(() => {
      setConferenceIndex((prev) => (prev + 1) % conferences.length);
    }, 20000);
    return () => clearInterval(interval);
  }, [conferences]);

  const navigate = useNavigate();
  const videoRef = useRef(null);

  useEffect(() => {
    const remembered = localStorage.getItem("rememberedEmail");
    const emailFromLocal = localStorage.getItem("userEmail");
    const emailFromSession = sessionStorage.getItem("userEmail");
    const stored = remembered || emailFromLocal || emailFromSession;
    if (stored) setUsername(stored);
    const pendingError = sessionStorage.getItem("verificationError");
    const pendingEmail = sessionStorage.getItem("verificationEmailPending");
    if (pendingError) {
      setError(pendingError);
      if (pendingEmail) setVerificationPendingEmail(pendingEmail);
      sessionStorage.removeItem("verificationError");
      sessionStorage.removeItem("verificationEmailPending");
    }
  }, []);

  const isAuthed = () =>
    !!tokenStorage.get() ||
    localStorage.getItem("isAuthenticated") === "true" ||
    sessionStorage.getItem("isAuthenticated") === "true";
  const openRegistration = () => {
    try {
      navigate("/register");
    } catch {
      setShowRegister(true);
    }
  };

  const [postIndex, setPostIndex] = useState(0); // Individual index for news posts
  const [showIframe, setShowIframe] = useState(false);
  const [iframeUrl] = useState("");
  // Registration overlay state
  const [registerEmail, setRegisterEmail] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [registerMessage, setRegisterMessage] = useState("");
  const [videoEnded] = useState(false);
  const [verificationPendingEmail, setVerificationPendingEmail] = useState("");
  const [resendLoading, setResendLoading] = useState(false);

  // Forgot Password overlay state
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMessage, setForgotMessage] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const fetchPosts = async () => {
      try {
        const response = await fetch(`${API_BASE}/wineries/news`, {
          headers: DEFAULT_HEADERS,
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error("Failed to fetch posts");
        }
        const data = await response.json();
        // Normalize, de-dup by title, then sort newest first
        const arr = Array.isArray(data) ? data : [];
        const normalized = arr
          .map((n) => ({
            ...n,
            date: n?.date_created,
            title: n?.title || "",
            source: (n?.source || "").toString().replace(/\s+/g, ""),
          }))
          .filter((n) => n.title);
        const deduped = Array.from(
          new Map(normalized.map((n) => [n.title, n])).values(),
        );
        deduped.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
        setPosts(deduped);
      } catch (error) {}
    };
    fetchPosts();
    return () => controller.abort();
  }, []);

  const formatNewsSource = (source) => {
    if (!source) return t("dashboard.news");
    const compact = source.toString().replace(/\s+/g, "");
    const spaced = compact.replace(/([A-Z])/g, " $1").trim();
    return spaced || t("dashboard.news");
  };

  // Cycle through news posts (individual to news)
  useEffect(() => {
    if (posts.length === 0) return;
    const interval = setInterval(() => {
      setPostIndex((prev) => (prev + 1) % posts.length);
    }, 20000);
    return () => clearInterval(interval);
  }, [posts]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 0.8;
      videoRef.current.play().catch(() => {
        // Handle autoplay restrictions gracefully
      });
    }
  }, [videoIndex]);

  const sha256Hex = async (text) => {
    const enc = new TextEncoder();
    const data = enc.encode(text);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  };

  const fetchVerificationStatusFromServer = async ({
    email,
    userId,
    token,
  }) => {
    const headers = {
      "Content-Type": "application/json",
      "X-API-KEY": "f3bb4afa55070cc052d1b888d4f7465a",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
    const attempts = [];
    if (email) {
      const qs = encodeURIComponent(email);
      // Prefer /api paths first
      attempts.push({
        url: `${API_BASE}/api/auth/email-verify/status?email=${qs}`,
        options: { method: "GET", headers },
      });
      attempts.push({
        url: `${API_BASE}/api/users/email-verify/status?email=${qs}`,
        options: { method: "GET", headers },
      });
      // Fallback to non-/api
      attempts.push({
        url: `${API_BASE}/auth/email-verify/status?email=${qs}`,
        options: { method: "GET", headers },
      });
      attempts.push({
        url: `${API_BASE}/users/email-verify/status?email=${qs}`,
        options: { method: "GET", headers },
      });
    }
    if (userId) {
      // Prefer /api POST
      attempts.push({
        url: `${API_BASE}/api/users/email-verify/status`,
        options: {
          method: "POST",
          headers,
          body: JSON.stringify({ user_id: userId }),
        },
      });
      // Fallback POST
      attempts.push({
        url: `${API_BASE}/users/email-verify/status`,
        options: {
          method: "POST",
          headers,
          body: JSON.stringify({ user_id: userId }),
        },
      });
    }
    for (const attempt of attempts) {
      try {
        const res = await fetch(attempt.url, attempt.options);
        if (!res.ok) continue;
        const data = await res.json().catch(() => null);
        if (!data) continue;
        // If the server tells us no account exists for this email, this is not a
        // verification problem. Return null so login proceeds and surfaces a
        // proper "invalid credentials / no account" error, instead of trapping
        // the user in a "please verify your email" dead-end (with a resend that
        // silently sends nothing because there is no account).
        if (data.exists === false) return null;
        // Normalize fields
        const raw =
          data.verified ??
          data.is_verified ??
          data.email_verified ??
          data.user?.is_verified ??
          data.profile?.is_verified ??
          null;
        if (typeof raw === "boolean") return raw;
        if (raw === 0 || raw === 1) return Boolean(raw);
        if (typeof raw === "string") {
          const normalized = raw.trim().toLowerCase();
          if (["true", "verified", "yes", "1"].includes(normalized))
            return true;
          if (
            ["false", "unverified", "pending", "0", "no"].includes(normalized)
          )
            return false;
        }
      } catch (_) {}
    }
    return null;
  };

  const handleResendVerification = async () => {
    const emailTarget =
      verificationPendingEmail || (username || "").trim() || email;
    if (!emailTarget) {
      toast({
        title: t("login_text.resend_email_required_title"),
        description: t("login_text.resend_email_required_desc"),
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    setResendLoading(true);
    try {
      const attempts = [
        `${API_BASE}/api/auth/email-verify/start`,
        `${API_BASE}/api/users/email-verify/start`,
        `${API_BASE}/auth/email-verify/start`,
        `${API_BASE}/users/email-verify/start`,
      ];
      let sent = false;
      let detail = "";
      for (const url of attempts) {
        try {
          const res = await fetch(url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-API-KEY": "f3bb4afa55070cc052d1b888d4f7465a",
            },
            body: JSON.stringify({ email: emailTarget }),
          });
          if (res.ok) {
            sent = true;
            break;
          }
          const text = await res.text().catch(() => "");
          try {
            detail = JSON.parse(text).error || text || detail;
          } catch (_) {
            detail = text || detail;
          }
        } catch (e) {
          detail = e.message || detail;
        }
      }
      if (sent) {
        toast({
          title: t("login_text.verification_email_sent_title"),
          description: t("login_text.verification_email_sent_desc"),
          status: "success",
          duration: 6000,
          isClosable: true,
        });
      } else {
        toast({
          title: t("login_text.verification_email_error_title"),
          description: detail || t("login_text.verification_email_error_desc"),
          status: "error",
          duration: 6000,
          isClosable: true,
        });
      }
    } finally {
      setResendLoading(false);
    }
  };

  const handleLogin = async () => {
    setError("");
    setVerificationPendingEmail("");
    const emailVal = (username || "").trim();
    const pwdVal = (password || "").trim();

    if (!emailVal) {
      setError(t("login_text.validation_email_required"));
      setShake(true);
      setTimeout(() => setShake(false), 500);
      return;
    }
    if (!pwdVal || pwdVal.length < 6) {
      setError(t("login_text.validation_password_invalid"));
      setShake(true);
      setTimeout(() => setShake(false), 500);
      return;
    }

    // Pre-flight: check verification status before attempting password auth
    try {
      const preStatus = await fetchVerificationStatusFromServer({
        email: emailVal,
      });
      if (preStatus === false) {
        setVerificationPendingEmail(emailVal);
        setError(t("login_text.verify_required_message"));
        setShake(true);
        setTimeout(() => setShake(false), 500);
        return;
      }
    } catch (_) {
      // Ignore network errors here; we'll check again after a successful auth
    }

    try {
      const password_hash = await sha256Hex(pwdVal);

      // Try a set of likely verification endpoints. We saw 405 on /auth/login,
      // so prioritize /users/verify with password_hash first.
      const attempts = [
        {
          url: "https://api.globalwineinstitute.org/users/verify",
          body: { email: emailVal, password_hash },
        },
        {
          url: "https://api.globalwineinstitute.org/users/login",
          body: { email: emailVal, password: pwdVal },
        },
        {
          url: "https://api.globalwineinstitute.org/auth/verify",
          body: { email: emailVal, password_hash },
        },
        {
          url: "https://api.globalwineinstitute.org/auth/login",
          body: { email: emailVal, password: pwdVal },
        },
      ];

      let authed = false;
      let detail = "";
      let authToken = null;
      let authUser = { id: null, email: "", username: "" };
      let lastSuccessPayload = null;

      for (const { url, body } of attempts) {
        try {
          const res = await fetch(url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-API-KEY": "f3bb4afa55070cc052d1b888d4f7465a",
            },
            body: JSON.stringify(body),
          });

          let data = null;
          try {
            data = await res.json();
          } catch {}

          if (res.status === 405) {
            // Method Not Allowed on this route — try the next candidate
            detail = `Method not allowed at ${url}`;
            continue;
          }

          if (res.ok && data) {
            if (data.token) authToken = data.token;
            if (data.user) {
              authUser.id = data.user.id ?? null;
              authUser.email = data.user.email ?? "";
              authUser.username = data.user.username ?? "";
            }
            if (data.profile) {
              // you already fetch profile later, so just keep user here
            }
            lastSuccessPayload = data;
            // Do not mark as authenticated yet; we'll confirm verification status after the loop.
            authed = true;
            break;
          }

          // Capture server-provided error to show user
          if (data && (data.error || data.message)) {
            detail = data.error || data.message;
          } else if (!res.ok) {
            detail = `HTTP ${res.status}`;
          }
        } catch (e) {
          detail = e.message || detail;
        }
      }

      // if (blockedByVerification) {
      //   setVerificationPendingEmail(blockedEmail || emailVal);
      //   setError(
      //     blockedMessage ||
      //       "You need to verify your email first. Please check your inbox."
      //   );
      //   setShake(true);
      //   setTimeout(() => setShake(false), 500);
      //   return;
      // }

      if (!authed) {
        setError(
          detail
            ? t("login_text.login_failed_with_detail", { detail })
            : t("login_text.login_failed_default"),
        );
        setShake(true);
        setTimeout(() => setShake(false), 500);
        return;
      }

      // Success
      if (!authUser?.email) authUser.email = emailVal;

      // Prefer authoritative server status before persisting any auth
      const remoteStatus = await fetchVerificationStatusFromServer({
        email: authUser?.email || emailVal,
        userId: authUser?.id,
        token: authToken,
      });
      // Fall back to interpreting the immediate payload only if the remote check was inconclusive
      let verifiedFlag =
        typeof remoteStatus === "boolean"
          ? remoteStatus
          : [
              lastSuccessPayload?.user?.is_verified,
              lastSuccessPayload?.is_verified,
              lastSuccessPayload?.profile?.is_verified,
              lastSuccessPayload?.email_verified,
              lastSuccessPayload?.user?.email_verified,
            ].some((v) => {
              if (typeof v === "boolean") return v;
              if (v === 0 || v === 1) return Boolean(v);
              if (typeof v === "string") {
                const normalized = v.trim().toLowerCase();
                if (["true", "verified", "yes", "1"].includes(normalized))
                  return true;
                if (
                  ["false", "unverified", "pending", "0", "no"].includes(
                    normalized,
                  )
                )
                  return false;
              }
              return null;
            }) || null;

      if (verifiedFlag === false || verifiedFlag === null) {
        setVerificationPendingEmail(authUser?.email || emailVal);
        setError(t("login_text.verify_required_message"));
        tokenStorage.clear();
        localStorage.removeItem("isAuthenticated");
        sessionStorage.removeItem("isAuthenticated");
        setShake(true);
        setTimeout(() => setShake(false), 500);
        return;
      }

      // Persist token according to rememberMe (Option B)
      tokenStorage.set(authToken, rememberMe);

      // Legacy flags for your existing app flow
      if (rememberMe) {
        localStorage.setItem("isAuthenticated", "true");
        localStorage.setItem("userEmail", emailVal);
        localStorage.setItem("rememberMe", "true");
        localStorage.setItem("rememberedEmail", emailVal);
        sessionStorage.removeItem("isAuthenticated");
        sessionStorage.removeItem("userEmail");
      } else {
        sessionStorage.setItem("isAuthenticated", "true");
        sessionStorage.setItem("userEmail", emailVal);
        localStorage.setItem("rememberMe", "false");
        localStorage.removeItem("isAuthenticated");
        localStorage.removeItem("userEmail");
        localStorage.removeItem("rememberedEmail");
      }
      const storage = rememberMe ? localStorage : sessionStorage;
      const otherStorage = rememberMe ? sessionStorage : localStorage;
      try {
        otherStorage.removeItem("userprofile");
        otherStorage.removeItem("userId");
        otherStorage.removeItem("userEmail");
      } catch (_) {}

      try {
        const headers = {
          "Content-Type": "application/json",
          ...DEFAULT_HEADERS,
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
          ...(authUser?.id ? { "X-User-Id": String(authUser.id) } : {}),
        };
        const emailForQuery = authUser?.email || emailVal || "";
        if (emailForQuery) {
          headers["X-User-Email"] = emailForQuery;
        }
        const profileRes = await fetch(`${API_BASE}/wineries/profile`, {
          method: "GET",
          headers,
        });
        let payload = null;
        if (profileRes.ok) {
          payload = await profileRes.json();
        } else {
          let errorText = null;
          try {
            errorText = await profileRes.text();
          } catch (_) {}
          console.warn("[Login] /wineries/profile fetch failed", {
            status: profileRes.status,
            errorText,
          });
        }

        if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
          payload = {
            user: {
              id: authUser?.id ?? null,
              email: authUser?.email || emailVal || "",
              username: authUser?.username || "",
            },
            profile: null,
          };
        }

        const serialized = JSON.stringify(payload || {});
        storage.setItem("userprofile", serialized);
        const resolvedId =
          payload?.user?.id ?? payload?.profile?.id ?? authUser?.id ?? null;
        if (resolvedId != null) {
          storage.setItem("userId", String(resolvedId));
        }
        const resolvedEmail =
          payload?.user?.email ||
          payload?.profile?.email ||
          authUser?.email ||
          emailVal ||
          "";
        if (resolvedEmail) {
          storage.setItem("userEmail", resolvedEmail);
        }
      } catch (_) {
        const fallbackUser = {
          id: authUser?.id ?? null,
          email: authUser?.email || emailVal || "",
          username: authUser?.username || "",
        };
        try {
          const serialized = JSON.stringify({
            user: fallbackUser,
            profile: null,
          });
          storage.setItem("userprofile", serialized);
          if (fallbackUser.id != null)
            storage.setItem("userId", String(fallbackUser.id));
          if (fallbackUser.email)
            storage.setItem("userEmail", fallbackUser.email);
          console.warn("[Login] Stored fallback userprofile payload", {
            storage: rememberMe ? "localStorage" : "sessionStorage",
            fallback: fallbackUser,
          });
        } catch (_) {}
      }

      setIsLoggedIn(true);
      try {
        const { trackEvent } = await import("./services/trackingService");
        trackEvent("login");
      } catch (_) {}
    } catch (e) {
      setError(t("login_text.login_generic_error"));
      setShake(true);
      setTimeout(() => setShake(false), 500);
    }
  };

  const handleForgotSubmit = async () => {
    setForgotMessage("");
    const emailVal = (forgotEmail || "").trim();
    if (!emailVal) {
      setForgotMessage(t("login_text.forgot_email_required"));
      return;
    }
    setForgotLoading(true);
    try {
      const attempts = [
        {
          url: "https://api.globalwineinstitute.org/users/password-reset",
          body: { email: emailVal },
        },
        {
          url: "https://api.globalwineinstitute.org/auth/password-reset",
          body: { email: emailVal },
        },
        {
          url: "https://api.globalwineinstitute.org/users/forgot-password",
          body: { email: emailVal },
        },
        {
          url: "https://api.globalwineinstitute.org/auth/forgot-password",
          body: { email: emailVal },
        },
        {
          url: "https://api.globalwineinstitute.org/users/reset/start",
          body: { email: emailVal },
        },
      ];
      let ok = false;
      let detail = "";
      for (const { url, body } of attempts) {
        try {
          const res = await fetch(url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-API-KEY": "f3bb4afa55070cc052d1b888d4f7465a",
            },
            body: JSON.stringify(body),
          });
          let data = null;
          try {
            data = await res.json();
          } catch {}
          if (res.ok) {
            ok = true;
            break;
          }
          if (data && (data.error || data.message))
            detail = data.error || data.message;
        } catch (e) {
          detail = e.message || detail;
        }
      }
      if (ok) {
        setForgotMessage(t("login_text.forgot_success"));
      } else {
        setForgotMessage(
          detail
            ? t("login_text.forgot_error_with_detail", { detail })
            : t("login_text.forgot_error_default"),
        );
      }
    } finally {
      setForgotLoading(false);
    }
  };
  return (
    <Box position="relative" minHeight="100vh" overflowY="auto">
      <input style={{ opacity: 0, position: "absolute" }} />
      <video
        key={videoIndex}
        ref={videoRef}
        autoPlay
        poster={videoSources[videoIndex].replace(".mp4", "_poster.webp")}
        muted
        playsInline
        loop
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          zIndex: -3,
          opacity: videoEnded ? 0 : 1,
          transition: "opacity 3s ease-out",
        }}
      >
        <source src={videoSources[videoIndex]} type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Semi-transparent overlay to darken the background */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="rgba(10,46,77,0.0)"
        zIndex={0}
        pointerEvents="none"
      />

      <Box
        position="absolute"
        top={0}
        left={0}
        width="100%"
        height="100%"
        bg="#3a1714"
        opacity={videoEnded ? 1 : 0}
        transition="opacity 2.5s ease-in-out"
        zIndex={-2}
      />

      <Box
        position="absolute"
        top={0}
        left={0}
        width="100%"
        height="100%"
        bg="linear-gradient(to bottom, rgba(6, 41, 64, 0.0), rgba(6, 41, 64, 0.0))"
        zIndex={-2}
      />

      <Box
        position="absolute"
        top={0}
        left={0}
        width="100%"
        height="100%"
        backgroundImage="url('/grain.png')"
        backgroundRepeat="repeat"
        opacity={0.1}
        zIndex={-1}
        pointerEvents="none"
      />
      {/* Unified Flex container for Login, Insights, News, Conferences, Research cards */}
      <Flex
        direction={{ base: "column", lg: "row" }}
        wrap="wrap"
        gap={{ base: 6, md: 8, lg: 10 }}
        px={{ base: 4, md: 10, lg: 16 }}
        py={{ base: 6, md: 8, lg: 10 }}
        justify="center"
        align={{ base: "stretch", lg: "flex-start" }}
        width="100%"
        maxW="1600px"
        mx="auto"
        mt="40px"
        zIndex={1}
        position="relative"
      >
        {/* Login Box */}
        <Box
          // key="card-login"
          flex="1 1 350px"
          width={{ base: "100%", md: "400px" }}
          height={{ base: "auto", md: "420px" }}
          p={{ base: 5, md: 6 }}
          borderRadius="2xl"
          bg="rgba(255, 255, 255, 0.2)"
          _hover={{ bg: "rgba(255, 255, 255, 0.3)" }}
          backdropFilter="blur(12px)"
          border="1px solid rgba(255, 255, 255, 0.2)"
          boxShadow="0 8px 32px rgba(0, 0, 0, 0.2)"
          position="relative"
          zIndex={1}
          className={shake ? "shake" : ""}
          display="flex"
          flexDirection="column"
          justifyContent="flex-start"
          overflow={{ base: "visible", md: "hidden" }}
          mt={0}
          pb={{ base: 4, md: 0 }}
        >
          {/* Top-right actions inside login box */}
          <HStack
            spacing={1.5}
            justifyContent="space-between"
            zIndex={99}
            mb={-2}
          >
            <HStack
              spacing={1}
              divider={
                <Box h="3" borderLeftWidth="1px" borderColor="gray.400" />
              }
            >
              {LANGUAGE_CODES.map((lang) => (
                <Button
                  key={lang}
                  size="xs"
                  h="22px"
                  minW="28px"
                  px={1}
                  fontSize="10px"
                  variant={i18n.language === lang ? "solid" : "ghost"}
                  colorScheme={i18n.language === lang ? "gwbi" : "gray"}
                  onClick={() => i18n.changeLanguage(lang)}
                  fontWeight="bold"
                  borderRadius="md"
                  _hover={{
                    bg: i18n.language === lang ? "gwbi.600" : "gray.200",
                  }}
                >
                  {lang.toUpperCase()}
                </Button>
              ))}
            </HStack>
            <Button size="sm" variant="ghost" onClick={openRegistration}>
              {t("register")}
            </Button>
          </HStack>
          {error && (
            <Box
              position="absolute"
              top="8px"
              left="8px"
              right="8px"
              zIndex={120}
              pointerEvents="auto"
            >
              <Alert
                status="error"
                variant="solid"
                borderRadius="md"
                boxShadow="md"
                flexDirection="column"
                alignItems="flex-start"
                gap={2}
                position="relative"
              >
                <CloseButton
                  position="absolute"
                  top="4px"
                  right="4px"
                  size="sm"
                  color="white"
                  onClick={() => {
                    setError("");
                    setVerificationPendingEmail("");
                  }}
                />
                <HStack align="flex-start">
                  <AlertIcon />
                  <Text>{error}</Text>
                </HStack>
                {verificationPendingEmail && (
                  <>
                    <Text fontSize="sm" color="yellow.100">
                      {t("login_text.account_not_verified")}
                    </Text>
                    <Button
                      size="xs"
                      bg="gwbi.300"
                      color="white"
                      _hover={{ bg: "gwbi.400", color: "white" }}
                      border="1px solid"
                      borderColor="gwbi.500"
                      onClick={handleResendVerification}
                      isLoading={resendLoading}
                    >
                      {t("login_text.resend_verification_button")}
                    </Button>
                  </>
                )}
              </Alert>
            </Box>
          )}
          <Flex
            direction="column"
            align="center"
            position="relative"
            width="100%"
            mt="14px"
          >
            {/* winelogo_new.png - now above the login fields and smaller */}
            <Image
              src="/gwbi_logo_red.png"
              alt="Wine Logo"
              boxSize="100px"
              objectFit="contain"
              filter="brightness(0) invert(1)"
              mt="8px"
              mb="8px"
              zIndex={0}
              pointerEvents="none"
              transform="scale(1.4)"
              transformOrigin={"center"}
            />
          </Flex>

          <form
            onSubmit={(e) => {
              e.preventDefault(); // evita il reload della pagina
              handleLogin(); // tua logica di login
            }}
          >
            <Box
              mt={{ base: 0, md: "-10px" }}
              width="100%"
              position="relative"
              zIndex={5}
            >
              <FormControl mb={1}>
                <FormLabel>{t("email")}</FormLabel>
                <Input
                  type="text"
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    sessionStorage.setItem("userEmail", e.target.value);
                  }}
                  variant="outline"
                  borderColor="#5f2323"
                  placeholder="Enter your email"
                  position="relative"
                  zIndex={6}
                />
              </FormControl>

              <FormControl mb={3}>
                <FormLabel>{t("password")}</FormLabel>
                <InputGroup>
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    variant="outline"
                    borderColor="#5f2323"
                    pr="2.5rem"
                  />
                  <InputRightElement h="100%">
                    <IconButton
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      icon={showPassword ? <FiEyeOff /> : <FiEye />}
                      size="sm"
                      variant="ghost"
                      color="gray.500"
                      _hover={{ color: "#5f2323" }}
                      onClick={() => setShowPassword((v) => !v)}
                      tabIndex={-1}
                    />
                  </InputRightElement>
                </InputGroup>
              </FormControl>

              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={4}
              >
                <Checkbox
                  isChecked={rememberMe}
                  onChange={(e) => {
                    const val = e.target.checked;
                    setRememberMe(val);
                    localStorage.setItem("rememberMe", val ? "true" : "false");
                    if (val && username) {
                      localStorage.setItem("rememberedEmail", username);
                    } else if (!val) {
                      localStorage.removeItem("rememberedEmail");
                    }
                  }}
                  colorScheme="gwbi"
                  color="#5f2323"
                  fontSize="sm"
                  sx={{
                    ".chakra-checkbox__control": {
                      borderColor: "#5f2323",
                      borderWidth: "1px",
                    },
                  }}
                >
                  {t("remember_me")}
                </Checkbox>

                <Link
                  href="#"
                  color="#5f2323"
                  fontSize="sm"
                  textDecoration="underline"
                  onClick={(e) => {
                    e.preventDefault();
                    setForgotEmail(username || "");
                    setShowForgot(true);
                  }}
                >
                  {t("forgot_password")}
                </Link>
              </Box>
            </Box>

            <Button
              type="submit" // <— unico bottone Accedi
              size="sm"
              colorScheme="gwbi"
              variant="solid"
            >
              {t("login")}
            </Button>
          </form>
        </Box>
        <Box
          zIndex={1}
          position="relative"
          flex="1 1 350px"
          width={{ base: "100%", md: "400px" }}
          height={{ base: "auto", md: "420px" }}
          display="flex"
          flexDirection="column"
          justifyContent="flex-start"
          mt={0}
        >
          <VisionCard openRegistration={openRegistration} />
        </Box>

        {/* Weekly Wine Summary Card */}
        {weeklySummary && (
          <Box
            flex="1 1 350px"
            width={{ base: "100%", md: "400px" }}
            height={{ base: "auto", md: "420px" }}
            overflow="hidden"
            p="20px"
            borderRadius="2xl"
            bg="rgba(0, 0, 0, 0.45)"
            _hover={{ bg: "rgba(0, 0, 0, 0.55)" }}
            backdropFilter="blur(12px)"
            border="1px solid rgba(255, 255, 255, 0.12)"
            boxShadow="0 8px 32px rgba(0, 0, 0, 0.2)"
            display="flex"
            flexDirection="column"
            mt={0}
          >
            {/* Header */}
            <Box mb={3} flexShrink={0}>
              <Text fontSize="10px" fontWeight="600" color="whiteAlpha.600" textTransform="uppercase" letterSpacing="0.12em">
                {t("weekly_summary.week_label", "Week {{week}}, {{year}}", {
                  week: weeklySummary.week,
                  year: weeklySummary.year,
                })}
              </Text>
              <Text fontSize="md" fontWeight="800" color="white" lineHeight="1.2" mt={0.5}>
                {t("weekly_summary.title", "This Week in Wine")}
              </Text>
            </Box>

            {/* Tab pills */}
            <HStack spacing={1} mb={3} flexShrink={0}>
              {[
                { key: "journal", label: t("weekly_summary.journal_tab", "Business") },
                { key: "academic", label: t("weekly_summary.academic_tab", "Academic") },
                { key: "policy", label: t("weekly_summary.policy_tab", "Policy") },
              ].map((tab) => (
                <Text
                  key={tab.key}
                  fontSize="10px"
                  fontWeight="600"
                  color="whiteAlpha.800"
                  bg="rgba(255, 255, 255, 0.12)"
                  px={2.5}
                  py={1}
                  borderRadius="full"
                  textTransform="uppercase"
                  letterSpacing="0.05em"
                >
                  {tab.label}
                </Text>
              ))}
            </HStack>

            {/* Scrollable content */}
            <Box flex={1} overflowY="auto" pr={1}
              sx={{
                "&::-webkit-scrollbar": { width: "4px" },
                "&::-webkit-scrollbar-thumb": { bg: "whiteAlpha.300", borderRadius: "full" },
              }}
            >
              {weeklySummary.journal_news && (
                <Box mb={3}>
                  <Text fontSize="xs" fontWeight="700" color="white" mb={1}>
                    {t("weekly_summary.journal_tab", "Business")}
                  </Text>
                  {renderMarkdownLogin(weeklySummary.journal_news)}
                </Box>
              )}
              {weeklySummary.academic_news && (
                <Box mb={3} pt={2} borderTop="1px solid" borderColor="whiteAlpha.200">
                  <Text fontSize="xs" fontWeight="700" color="white" mb={1}>
                    {t("weekly_summary.academic_tab", "Academic News")}
                  </Text>
                  {renderMarkdownLogin(weeklySummary.academic_news)}
                </Box>
              )}
              {weeklySummary.policy_news && (
                <Box mb={3} pt={2} borderTop="1px solid" borderColor="whiteAlpha.200">
                  <Text fontSize="xs" fontWeight="700" color="white" mb={1}>
                    {t("weekly_summary.policy_tab", "Policy News")}
                  </Text>
                  {renderMarkdownLogin(weeklySummary.policy_news)}
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Research Box */}
        <Box
          //  key="card-research"
          flex="1 1 350px"
          width={{ base: "100%", md: "400px" }}
          height={{ base: "auto", md: "420px" }}
          overflow={{ base: "visible", md: "hidden" }}
          p="16px"
          borderWidth="1px"
          borderRadius="2xl"
          bg="rgba(255, 255, 255, 0.15)"
          _hover={{ bg: "rgba(255, 255, 255, 0.25)" }}
          backdropFilter="blur(12px)"
          border="1px solid rgba(255, 255, 255, 0.2)"
          boxShadow="0 8px 32px rgba(0, 0, 0, 0.2)"
          position="static"
          display="flex"
          flexDirection="column"
          justifyContent="flex-start"
          textAlign="left"
          transition="all 4.0s ease-in-out"
          opacity={publications && publications.length > 0 ? 1 : 0}
          mt={0}
        >
          <Flex align="center" mb={2} gap={2} pb={3}>
            {publications &&
              publications[publicationIndex] &&
              publications[publicationIndex].journal && (
                <Image
                  src={`/${publications[publicationIndex].journal
                    .replace(/[^a-z0-9]/gi, "")
                    .toLowerCase()}.png`}
                  alt={publications[publicationIndex].journal}
                  height="32px"
                  width="72px"
                  objectFit="contain"
                  onError={(e) => {
                    const n = Math.floor(Math.random() * 4) + 1;
                    e.currentTarget.src = `/basic_${n}.png`;
                  }}
                />
              )}
            <Box mr="20px">
              <Text fontSize="sm" color="white">
                {publications &&
                publications.length > 0 &&
                publications[publicationIndex]
                  ? publications[publicationIndex].journal
                  : ""}
              </Text>
            </Box>
            <Text
              bg="rgba(0, 0, 0, 0.6)"
              color="white"
              px={2}
              py={1}
              fontSize="sm"
              borderRadius="md"
              zIndex={2}
              ml="auto"
            >
              {t("research")}
            </Text>
          </Flex>
          {/* Section label for research */}
          <Heading
            fontSize="xl"
            color="white"
            fontWeight="bold"
            fontFamily="Georgia, 'Times New Roman', serif"
            fontStyle="italic"
            mb={2}
          >
            {publications &&
            publications.length > 0 &&
            publications[publicationIndex]
              ? `'${publications[publicationIndex].insights}'`
              : ""}
          </Heading>
          <Text fontSize="xs" color="white" mb={1}>
            {publications &&
            publications.length > 0 &&
            publications[publicationIndex]
              ? new Date(
                  publications[publicationIndex].date_published,
                ).toLocaleDateString(locale, {
                  weekday: "short",
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })
              : ""}
          </Text>
          <Text fontSize="xs" color="white">
            {publications &&
            publications.length > 0 &&
            publications[publicationIndex]
              ? publications[publicationIndex].author
              : ""}
          </Text>
        </Box>
        {/* News Card */}
        <Box
          flex="1 1 350px"
          width={{ base: "100%", md: "400px" }}
          height={{ base: "auto", md: "420px" }}
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          overflow={{ base: "visible", md: "hidden" }}
          borderWidth="1px"
          borderRadius="2xl"
          bg="rgba(255, 255, 255, 0.15)"
          _hover={showNews ? { bg: "rgba(255, 255, 255, 0.25)" } : {}}
          backdropFilter="blur(12px)"
          border="1px solid rgba(255, 255, 255, 0.2)"
          boxShadow="0 8px 32px rgba(0, 0, 0, 0.2)"
          zIndex={1}
          position="static"
          mt={0}
        >
          {showNews && posts.length > 0 && posts[postIndex] ? (
            <>
              <Box position="relative" width="100%">
                <Image
                  src={posts[postIndex]?.image || "/news_fallback.jpg"}
                  onError={(e) => {
                    const n = Math.floor(Math.random() * 4) + 1;
                    e.currentTarget.src = `/basic_${n}.png`;
                  }}
                  alt={posts[postIndex]?.title || "News"}
                  width="100%"
                  height="220px"
                  objectFit="cover"
                  borderRadius="xl"
                  margin="0"
                  transition="opacity 18.0s ease-in-out"
                />
                <Box
                  position="absolute"
                  top="8px"
                  left="8px"
                  bg="rgba(0, 0, 0, 0.6)"
                  color="white"
                  px={2}
                  py={1}
                  fontSize="sm"
                  borderRadius="md"
                  zIndex={2}
                >
                  {formatNewsSource(posts[postIndex]?.source)}
                </Box>
                <Button
                  size="sm"
                  colorScheme="gwbi"
                  variant="solid"
                  position="absolute"
                  bottom="8px"
                  right="8px"
                  onClick={openRegistration}
                >
                  {t("get_started")}
                </Button>
              </Box>
              <Box p={{ base: 4, md: 5 }} flex="1" overflow="auto">
                <HStack justify="space-between" align="center" mb={2}>
                  <HStack spacing={2}>
                    <Image
                      src={`/${(posts[postIndex]?.source || "generic")
                        .toString()
                        .replace(/[^a-z0-9]/gi, "")
                        .toLowerCase()}.png`}
                      alt={posts[postIndex]?.source || "News source"}
                      height="32px"
                      width="72px"
                      objectFit="contain"
                      borderRadius="xl"
                      onError={(e) => {
                        const n = Math.floor(Math.random() * 4) + 1;
                        e.currentTarget.src = `/basic_${n}.png`;
                      }}
                    />
                    <Box fontSize="sm" fontWeight="bold" color="gray.800">
                      {formatNewsSource(posts[postIndex]?.source)}
                    </Box>
                  </HStack>
                </HStack>
                <Box borderBottom="1px solid" borderColor="gray.500" mt={2} />
                <Box fontSize="xs" color="gray.800" pt={2}>
                  {new Date(posts[postIndex].date).toLocaleDateString(locale, {
                    weekday: "short",
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}{" "}
                  – {posts[postIndex]?.newspaper}
                </Box>
                <Heading size="sm" mt={1} mb={2} color="gray.800">
                  {posts[postIndex].title}
                </Heading>
              </Box>
              {/* Navigation for News */}
              <HStack
                justify="center"
                align="center"
                spacing={2}
                py={2}
                bg="transparent"
              >
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() =>
                    setPostIndex((prev) =>
                      prev === 0 ? posts.length - 1 : prev - 1,
                    )
                  }
                  isDisabled={posts.length <= 1}
                >
                  &lt;
                </Button>
                {Array.from({ length: 4 }).map((_, idx) => {
                  const activeDot = postIndex % 4;
                  const isActive = idx === activeDot;
                  return (
                    <Box
                      key={`news-dot-${idx}`}
                      w={isActive ? 4 : 2}
                      h={isActive ? 4 : 2}
                      borderRadius="full"
                      bg={isActive ? "gwbi.500" : "gray.300"}
                      mx={1}
                      transition="all 0.4s ease"
                    />
                  );
                })}
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() =>
                    setPostIndex((prev) =>
                      prev === posts.length - 1 ? 0 : prev + 1,
                    )
                  }
                  isDisabled={posts.length <= 1}
                >
                  &gt;
                </Button>
              </HStack>
            </>
          ) : (
            <VStack spacing={0} align="stretch" w="100%" h="100%">
              <Skeleton
                h="220px"
                w="100%"
                borderRadius="xl"
                startColor="whiteAlpha.100"
                endColor="whiteAlpha.300"
              />
              <VStack align="stretch" spacing={3} p={5} flex={1}>
                <HStack spacing={2}>
                  <Skeleton h="32px" w="72px" borderRadius="md" startColor="whiteAlpha.100" endColor="whiteAlpha.300" />
                  <Skeleton h="14px" w="100px" borderRadius="sm" startColor="whiteAlpha.100" endColor="whiteAlpha.300" />
                </HStack>
                <Skeleton h="1px" w="100%" startColor="whiteAlpha.100" endColor="whiteAlpha.200" />
                <Skeleton h="12px" w="180px" borderRadius="sm" startColor="whiteAlpha.100" endColor="whiteAlpha.300" />
                <SkeletonText noOfLines={2} spacing={2} skeletonHeight="14px" startColor="whiteAlpha.100" endColor="whiteAlpha.300" />
              </VStack>
            </VStack>
          )}
        </Box>
        {/* Events Card */}
        <Box
          flex="1 1 350px"
          width={{ base: "100%", md: "400px" }}
          height={{ base: "auto", md: "420px" }}
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          overflow={{ base: "visible", md: "hidden" }}
          borderWidth="1px"
          borderRadius="2xl"
          bg="rgba(255, 255, 255, 0.15)"
          _hover={showConferences ? { bg: "rgba(255, 255, 255, 0.25)" } : {}}
          backdropFilter="blur(12px)"
          border="1px solid rgba(255, 255, 255, 0.2)"
          boxShadow="0 8px 32px rgba(0, 0, 0, 0.2)"
          zIndex={1}
          position="static"
          mt={0}
        >
          {showConferences &&
          conferences.length > 0 &&
          conferences[conferenceIndex] ? (
            <>
              <Box position="relative" width="100%">
                <Image
                  src={`/conference_${Math.floor(Math.random() * 20) + 1}.png`}
                  alt="Event"
                  width="100%"
                  height="220px"
                  objectFit="cover"
                  borderRadius="0"
                  margin="0"
                  transition="opacity 1.0s ease-in-out"
                  onError={(e) => {
                    const n = Math.floor(Math.random() * 4) + 1;
                    e.currentTarget.src = `/basic_${n}.png`;
                  }}
                />
                <Box
                  position="absolute"
                  top="8px"
                  left="8px"
                  bg="rgba(0, 0, 0, 0.6)"
                  color="white"
                  px={2}
                  py={1}
                  fontSize="sm"
                  borderRadius="md"
                  zIndex={2}
                >
                  {t("conferences")}
                </Box>
                <Button
                  size="sm"
                  colorScheme="gwbi"
                  variant="solid"
                  position="absolute"
                  bottom="8px"
                  right="8px"
                  onClick={openRegistration}
                >
                  {t("see_details")}
                </Button>
              </Box>
              <Box p={{ base: 4, md: 5 }} flex="1" overflow="auto">
                <HStack justify="space-between" align="center" mb={2}>
                  <HStack spacing={2}>
                    <Image
                      src="/icon_conference.webp"
                      alt="Event Icon"
                      boxSize="32px"
                      objectFit="contain"
                      onError={(e) => {
                        const n = Math.floor(Math.random() * 4) + 1;
                        e.currentTarget.src = `/basic_${n}.png`;
                      }}
                    />
                    <Box fontSize="sm" fontWeight="bold" color="gray.800">
                      {conferences[conferenceIndex].name}
                    </Box>
                  </HStack>
                </HStack>
                <Box borderBottom="1px solid" borderColor="gray.500" mt={2} />
                <Box fontSize="xs" color="gray.800" pt={2}>
                  {new Date(
                    conferences[conferenceIndex].date_conference,
                  ).toLocaleDateString(locale, {
                    weekday: "short",
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}{" "}
                  – {conferences[conferenceIndex].location}
                </Box>
                <Heading size="sm" mt={1} mb={2} color="gray.800">
                  {conferences[conferenceIndex].focus}
                </Heading>
              </Box>
              {/* Navigation for Events */}
              <HStack
                justify="center"
                align="center"
                spacing={2}
                py={2}
                bg="transparent"
              >
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() =>
                    setConferenceIndex((prev) =>
                      prev === 0 ? conferences.length - 1 : prev - 1,
                    )
                  }
                  isDisabled={conferences.length <= 1}
                >
                  &lt;
                </Button>
                {Array.from({ length: 4 }).map((_, idx) => {
                  const activeDot = conferenceIndex % 4;
                  const isActive = idx === activeDot;
                  return (
                    <Box
                      key={`conf-dot-${idx}`}
                      w={isActive ? 4 : 2}
                      h={isActive ? 4 : 2}
                      borderRadius="full"
                      bg={isActive ? "gwbi.500" : "gray.300"}
                      mx={1}
                      transition="all 0.4s ease"
                    />
                  );
                })}
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() =>
                    setConferenceIndex((prev) =>
                      prev === conferences.length - 1 ? 0 : prev + 1,
                    )
                  }
                  isDisabled={conferences.length <= 1}
                >
                  &gt;
                </Button>
              </HStack>
            </>
          ) : (
            <VStack spacing={0} align="stretch" w="100%" h="100%">
              <Skeleton
                h="220px"
                w="100%"
                startColor="whiteAlpha.100"
                endColor="whiteAlpha.300"
              />
              <VStack align="stretch" spacing={3} p={5} flex={1}>
                <HStack spacing={2}>
                  <Skeleton h="32px" w="32px" borderRadius="md" startColor="whiteAlpha.100" endColor="whiteAlpha.300" />
                  <Skeleton h="14px" w="140px" borderRadius="sm" startColor="whiteAlpha.100" endColor="whiteAlpha.300" />
                </HStack>
                <Skeleton h="1px" w="100%" startColor="whiteAlpha.100" endColor="whiteAlpha.200" />
                <Skeleton h="12px" w="200px" borderRadius="sm" startColor="whiteAlpha.100" endColor="whiteAlpha.300" />
                <SkeletonText noOfLines={2} spacing={2} skeletonHeight="14px" startColor="whiteAlpha.100" endColor="whiteAlpha.300" />
              </VStack>
            </VStack>
          )}
        </Box>


      </Flex>
      {showIframe && (
        <Box
          position="fixed"
          top="40px"
          left="40px"
          bottom="40px"
          right="40px"
          bg="rgba(255, 255, 255, 0.95)"
          zIndex={9999}
          borderRadius="2xl"
          overflow="hidden"
          display="flex"
          flexDirection="column"
        >
          <HStack
            justify="space-between"
            align="center"
            p={3}
            bg="white"
            borderBottom="1px solid #ccc"
          >
            <HStack spacing={3}>
              <Image src="/gwbi_favicon_red.png" alt="Logo" boxSize="40px" />
              <Text fontWeight="bold" fontSize="lg" color="#5f2323">
                Global Wineries Business Institute
              </Text>
            </HStack>
            <Button
              size="sm"
              colorScheme="gwbi"
              onClick={() => setShowIframe(false)}
            >
              Close
            </Button>
          </HStack>
          <Box flex="1">
            <iframe
              src={iframeUrl}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
              }}
              title="Article Frame"
            />
          </Box>
        </Box>
      )}
      {/* Forgot Password Overlay */}
      {showForgot && (
        <Box
          position="fixed"
          top="0"
          left="0"
          width="100vw"
          height="100vh"
          bg="rgba(0, 0, 0, 0.6)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          zIndex="9999"
        >
          <Box
            bg="#5f2323"
            p={6}
            borderRadius="md"
            width="90%"
            maxW="420px"
            boxShadow="lg"
          >
            <Heading size="md" mb={2} color="gray.300">
              {t("reset_title")}
            </Heading>
            <Text color="gray.300" fontSize="sm" mb={3}>
              {t("reset_desc")}
            </Text>
            <FormControl mb={3}>
              <FormLabel color="gray.300">{t("email")}</FormLabel>
              <Input
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                autoComplete="email"
                bg="white"
                color="gray.800"
              />
            </FormControl>
            {forgotMessage && (
              <Text color="gwbi.300" fontSize="sm" mb={3}>
                {forgotMessage}
              </Text>
            )}
            <HStack justify="space-between">
              <Button
                colorScheme="gwbi"
                isLoading={forgotLoading}
                loadingText="Sending"
                onClick={handleForgotSubmit}
              >
                {t("send_reset")}
              </Button>
              <Button
                variant="ghost"
                onClick={() => setShowForgot(false)}
                color="gray.300"
              >
                {t("cancel")}
              </Button>
            </HStack>
          </Box>
        </Box>
      )}
      {/* Registration Overlay */}
      {showRegister && (
        <Box
          position="fixed"
          top="0"
          left="0"
          width="100vw"
          height="100vh"
          bg="rgba(0, 0, 0, 0.6)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          zIndex="9999"
        >
          <Box
            bg="#5f2323"
            p={6}
            borderRadius="md"
            width="90%"
            maxW="400px"
            boxShadow="lg"
          >
            <Heading size="md" mb={4} color="gray.300">
              {t("create_account_title")}
            </Heading>
            <FormControl mb={3}>
              <FormLabel color="gray.300">{t("email")}</FormLabel>
              <Input
                type="email"
                value={registerEmail}
                onChange={(e) => setRegisterEmail(e.target.value)}
                autoComplete="email"
                bg="white"
                color="gray.800"
              />
            </FormControl>
            <FormControl mb={3}>
              <FormLabel color="gray.300">{t("password")}</FormLabel>
              <Input
                type="password"
                value={registerPassword}
                onChange={(e) => setRegisterPassword(e.target.value)}
                placeholder="Minimum 6 characters"
                bg="white"
                color="gray.800"
              />
            </FormControl>
            {registerMessage && (
              <Text color="gwbi.300" fontSize="sm" mb={3}>
                {registerMessage}
              </Text>
            )}
            <HStack justify="space-between">
              <Button
                colorScheme="gwbi"
                onClick={async () => {
                  try {
                    const password_hash = await sha256Hex(
                      registerPassword || "",
                    );
                    const res = await fetch(
                      "https://api.globalwineinstitute.org/users",
                      {
                        method: "POST",
                        headers: {
                          "Content-Type": "application/json",
                          "X-API-KEY": "f3bb4afa55070cc052d1b888d4f7465a",
                        },
                        body: JSON.stringify({
                          email: registerEmail,
                          password_hash,
                        }),
                      },
                    );
                    const data = await res.json();
                    if (!res.ok)
                      throw new Error(data.error || "Registration failed");
                    setRegisterMessage(
                      data.message || "Successfully registered!",
                    );
                    try {
                      navigate("/register");
                    } catch {}
                  } catch (err) {
                    setRegisterMessage("Something went wrong");
                  }
                }}
              >
                {t("register")}
              </Button>
              <Button
                variant="ghost"
                onClick={() => setShowRegister(false)}
                color="gray.300"
              >
                {t("cancel")}
              </Button>
            </HStack>
          </Box>
        </Box>
      )}
      {/* Floating CTA to encourage registration */}
      {!isAuthed() && !showRegister && (
        <Box position="fixed" right={8} bottom={{ base: 4, md: 8 }} zIndex={50}>
          <Button
            size="sm"
            colorScheme="gwbi"
            variant="solid"
            height="32px"
            fontSize="sm"
            boxShadow="0 8px 24px rgba(0,0,0,0.25)"
            onClick={openRegistration}
            alignSelf="flex-end"
          >
            {t("create_account_cta")}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default Login;
